/**
 * Help Button Component - the (?) menu in the top bar.
 *
 * KAN-619. This was a DaisyUI CSS-only dropdown (`dropdown dropdown-end`,
 * `tabindex="0"` on trigger and menu, `w-56` on the menu) - the same pattern
 * KAN-592 removed from the notification bell next to it, with the same defect.
 * `dropdown-end` right-anchors the 224px menu to the button's right edge, and
 * `<app-organization-switcher>` (`order-last grow basis-44` in
 * `main-layout.component.html`) sits BESIDE the icons at 414/430px, holding the
 * help button at x=152..200. So the menu started at 200 - 224 = -24 and every
 * item was cut at the left edge. Left overflow creates no horizontal page
 * scroll, so the app-shell overflow guard could not see it.
 *
 * The fix mirrors the bell (read `notification-bell.component.ts` for the long
 * form): an explicit open boolean with a real `<button>`, an `@if` menu that is
 * measured on open and placed with `position: fixed` coordinates from the pure,
 * shared `computeAnchoredPanelPlacement` (width cap + left-edge clamp), promoted
 * to the top layer with `popover="manual"` where supported, and dismissed by an
 * outside click, Escape, or the button itself. This also removes the
 * `:focus-within` two-click defect: one click on an item acts and closes the
 * menu.
 *
 * Zoneless: `menuOpen`/`placement` change inside DOM handlers, so each change is
 * followed by `cdr.detectChanges()`; the one after opening is a deliberate
 * synchronous force so the menu exists to be measured in the same handler.
 */

import { ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';

import { IconComponent } from '../icon/icon.component';
import {
  AnchoredPanelPlacement,
  computeAnchoredPanelPlacement,
} from '../../utils/anchored-panel-position.utils';

/** The menu's comfortable width whenever the viewport allows it (the old `w-56`). */
export const HELP_MENU_PREFERRED_WIDTH = 224;

let helpButtonUid = 0;

@Component({
  selector: 'app-help-button',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './help-button.component.html',
  styleUrl: './help-button.component.scss',
})
export class HelpButtonComponent implements OnDestroy {
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private hostRef = inject(ElementRef<HTMLElement>);

  @ViewChild('triggerEl') private triggerRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('menuEl') private menuRef?: ElementRef<HTMLUListElement>;

  readonly menuId = `help-button-menu-${helpButtonUid++}`;

  menuOpen = false;
  /** Off-screen until the first real measurement, so nothing flashes at 0,0. */
  placement: AnchoredPanelPlacement = { top: -9999, left: -9999, width: 0, maxHeight: 0 };

  private listenersAttached = false;

  private readonly onDocumentClick = (event: MouseEvent): void => {
    if (!this.menuOpen) return;
    const target = event.target as Node | null;
    if (target && this.hostRef.nativeElement.contains(target)) return;
    if (target && this.menuRef?.nativeElement.contains(target)) return;
    this.closeMenu();
  };

  private readonly onDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !this.menuOpen) return;
    event.preventDefault();
    this.closeMenu();
    this.triggerRef?.nativeElement.focus();
  };

  /** A resize moves the button (the header re-wraps); recompute rather than close. */
  private readonly onWindowResize = (): void => {
    if (this.menuOpen) this.positionMenu();
  };

  /**
   * A fixed menu would drift away from its button on scroll; close instead. The
   * menu's OWN scroll (a short landscape screen caps its height) is not a page
   * scroll and must not dismiss it.
   */
  private readonly onAncestorScroll = (event: Event): void => {
    if (!this.menuOpen) return;
    const target = event.target as Node | null;
    if (target && this.menuRef?.nativeElement.contains(target)) return;
    this.closeMenu();
  };

  ngOnDestroy(): void {
    this.detachGlobalListeners();
  }

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    if (this.menuOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  closeMenu(): void {
    if (!this.menuOpen) return;
    // hidePopover() throws InvalidStateError when the element is not shown.
    const menuEl = this.menuRef?.nativeElement;
    if (menuEl && typeof menuEl.hidePopover === 'function' && menuEl.matches(':popover-open')) {
      menuEl.hidePopover();
    }
    this.menuOpen = false;
    this.detachGlobalListeners();
    this.cdr.detectChanges();
  }

  navigateToHelp(): void {
    this.closeMenu();
    this.router.navigate(['/help']);
  }

  navigateToTutorials(): void {
    this.closeMenu();
    this.router.navigate(['/help'], { fragment: 'tutorials' });
  }

  navigateToFAQ(): void {
    this.closeMenu();
    this.router.navigate(['/help'], { fragment: 'faq' });
  }

  openKeyboardShortcuts(): void {
    this.closeMenu();
    // In a real app, this would open a modal with keyboard shortcuts
    alert('Keyboard Shortcuts:\n\n' +
          'Ctrl/Cmd + K: Quick Search\n' +
          'Ctrl/Cmd + /: Toggle Help\n' +
          'Esc: Close Dialogs\n' +
          'Arrow Keys: Navigate Lists');
  }

  reportIssue(): void {
    this.closeMenu();
    this.router.navigate(['/help'], { queryParams: { contact: 'true', category: 'technical' } });
  }

  private openMenu(): void {
    this.menuOpen = true;
    this.cdr.detectChanges();

    const menuEl = this.menuRef?.nativeElement;
    if (menuEl && typeof menuEl.showPopover === 'function') {
      menuEl.showPopover();
    }

    this.positionMenu();
    this.attachGlobalListeners();
  }

  private positionMenu(): void {
    const triggerEl = this.triggerRef?.nativeElement;
    if (!triggerEl) return;
    const rect = triggerEl.getBoundingClientRect();
    this.placement = computeAnchoredPanelPlacement(
      { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
      { width: window.innerWidth, height: window.innerHeight },
      { preferredWidth: HELP_MENU_PREFERRED_WIDTH },
    );
    this.cdr.detectChanges();
  }

  private attachGlobalListeners(): void {
    if (this.listenersAttached) return;
    this.listenersAttached = true;
    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('keydown', this.onDocumentKeydown, true);
    // scroll does not bubble - capture phase catches every ancestor's scroll.
    document.addEventListener('scroll', this.onAncestorScroll, true);
    window.addEventListener('resize', this.onWindowResize);
  }

  private detachGlobalListeners(): void {
    if (!this.listenersAttached) return;
    this.listenersAttached = false;
    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('keydown', this.onDocumentKeydown, true);
    document.removeEventListener('scroll', this.onAncestorScroll, true);
    window.removeEventListener('resize', this.onWindowResize);
  }
}
